// Pure cart helpers shared by CartContext and CartDrawer (unit-tested).

const GUEST_KEY = "cart_guest";

export const getCartStorageKey = (userId) => `cart_${userId || "guest"}`;

const readJson = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error(`Failed to read ${key} from storage:`, error);
    return null;
  }
};

/**
 * The cart and discount that belong to `userId`, read from storage. Nothing
 * from another account's key is ever returned. On sign-in, a guest cart (and
 * its discount, when the account has none saved) moves into the account and
 * the guest keys are removed.
 */
export const loadCartState = (storage, userId, normalizeItem = (i) => i) => {
  const key = getCartStorageKey(userId);
  const saved = readJson(storage, key);
  let items = Array.isArray(saved) ? saved : [];
  let discount = readJson(storage, `${key}_discount`);

  if (userId) {
    const guestItems = readJson(storage, GUEST_KEY);
    const guestDiscount = readJson(storage, `${GUEST_KEY}_discount`);
    if (Array.isArray(guestItems) && guestItems.length > 0) {
      const ids = new Set(items.map((item) => item.id));
      items = [...items, ...guestItems.filter((item) => !ids.has(item.id))].map(
        normalizeItem,
      );
    }
    if (!discount?.code && guestDiscount?.code) discount = guestDiscount;
    try {
      storage.removeItem(GUEST_KEY);
      storage.removeItem(`${GUEST_KEY}_discount`);
    } catch {
      /* storage unavailable */
    }
  }

  return {
    key,
    items,
    discountCode: discount?.code || "",
    discountInfo: discount?.code ? discount.info || null : null,
  };
};

/** A refresher in the cart is priced from its parent, which must be loaded. */
export const needsRefresherParent = (item) =>
  Boolean(
    item &&
    !item.isBundle &&
    item.is_refresher &&
    item.parent_course_id &&
    !item.parentCourseData,
  );

/**
 * Courses the cart would buy more than once: a course on its own and inside a
 * bundle, or in two bundles. `bundleCourses` maps bundle id → course list.
 * Mirrors findOverlappingCourseIds on the servers, which answer 409.
 */
export const findCartOverlaps = (cartItems, bundleCourses) => {
  const sources = new Map();
  const add = (course, source) => {
    if (!course?.id) return;
    const entry = sources.get(course.id) || {
      courseId: course.id,
      title: course.title,
      sources: [],
    };
    if (!entry.sources.includes(source)) entry.sources.push(source);
    sources.set(course.id, entry);
  };
  for (const item of cartItems || []) {
    if (item?.isBundle) {
      for (const course of bundleCourses.get(item.bundleId) || []) {
        add(course, item.title || item.name || "a bundle");
      }
    } else {
      add(item, "on its own");
    }
  }
  return [...sources.values()].filter((entry) => entry.sources.length > 1);
};

export const describeCartOverlap = (overlaps) => {
  if (!overlaps.length) return "";
  const [first] = overlaps;
  const where = first.sources
    .map((source) => (source === "on its own" ? source : `in "${source}"`))
    .join(" and ");
  const more =
    overlaps.length > 1
      ? ` (and ${overlaps.length - 1} more course${overlaps.length > 2 ? "s" : ""})`
      : "";
  return `"${first.title || "A course"}" is in your cart ${where}${more}. Remove the duplicate so you aren't charged twice.`;
};
